import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ExcelTable from './ExcelTable';
import './Table.css';

const DynamicTable = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [tableName, setTableName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    // Load table definition from localStorage
    const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
    const table = customTables.find(t => t.tableId === tableId);
    if (table) {
      setTableName(table.name);
    } else {
      // Fallback: try to get from table data
      const tableData = localStorage.getItem(`table_${tableId}`);
      if (tableData) {
        try {
          const data = JSON.parse(tableData);
          if (data && data[0]) {
            // Use a default name
            setTableName('New Table');
          }
        } catch (e) {
          console.error('Error loading table:', e);
        }
      }
    }

    // Listen for custom table name updates
    const handleNameUpdate = () => {
      const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
      const table = customTables.find(t => t.tableId === tableId);
      if (table) {
        setTableName(prevName => {
          if (prevName !== table.name) {
            return table.name;
          }
          return prevName;
        });
      }
    };
    
    window.addEventListener('customTablesUpdated', handleNameUpdate);
    return () => {
      window.removeEventListener('customTablesUpdated', handleNameUpdate);
    };
  }, [tableId]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameDoubleClick = () => {
    setIsEditingName(true);
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    // Save updated name to localStorage
    const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
    const tableIndex = customTables.findIndex(t => t.tableId === tableId);
    if (tableIndex !== -1) {
      customTables[tableIndex].name = tableName;
      localStorage.setItem('customTables', JSON.stringify(customTables));
      // Dispatch custom event to notify sidebar
      window.dispatchEvent(new Event('customTablesUpdated'));
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsEditingName(false);
      handleNameBlur();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleDeleteTable = () => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this table? This action cannot be undone.')) {
      return;
    }

    // Remove from customTables in localStorage
    const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
    const updatedCustomTables = customTables.filter(t => t.tableId !== tableId);
    localStorage.setItem('customTables', JSON.stringify(updatedCustomTables));

    // Remove table data from localStorage
    localStorage.removeItem(`table_${tableId}`);

    // Navigate to dashboard
    navigate('/dashboard');
  };

  // Load initial data from localStorage
  const loadInitialData = () => {
    const saved = localStorage.getItem(`table_${tableId}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading table data:', e);
      }
    }
    // Fallback: get from table definition
    const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
    const table = customTables.find(t => t.tableId === tableId);
    if (table && table.headers) {
      return [table.headers, Array(table.headers.length).fill('')];
    }
    return [['S no', 'Column 1', 'Notes'], ['', '', '']];
  };

  const initialData = loadInitialData();

  return (
    <div className="table-page">
      <div className="table-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          {isEditingName ? (
            <input
              ref={nameInputRef}
              type="text"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              className="table-name-input"
              maxLength={50}
              style={{ flex: 1 }}
            />
          ) : (
            <h1 onDoubleClick={handleNameDoubleClick} className="table-name-editable" title="Double-click to edit" style={{ flex: 1, margin: 0 }}>
              {tableName || 'New Table'}
              <span className="edit-icon">✎</span>
            </h1>
          )}
          <button
            onClick={handleDeleteTable}
            className="delete-table-header-btn"
            title="Delete this table"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      <ExcelTable 
        initialData={initialData} 
        currencyColumns={[]}
        fixedHeaders={true}
        totalColumns={[]}
        tableId={tableId}
        tableName={tableName}
      />
    </div>
  );
};

export default DynamicTable;

